import { useState } from 'react';

// Types
import type { CSSProperties, ComponentPropsWithoutRef, SVGAttributes } from 'react';

// Utils
import useRandomInterval, { random } from '@/utils/hooks/utils/useRandomInterval';

function range(start: number, end?: number, step: number = 1) {
    const output: number[] = [];
    if (typeof end === 'undefined') {
        end = start;
        start = 0;
    }
    for (let i = start; i < end; i += step) {
        output.push(i);
    }
    return output;
}

const colors = ['#0089ff', '#00b1ff', '#00d5ff'];

type SparkleType = {
    id: string;
    createdAt: number;
    color: string;
    size: number;
    style: {
        top: string;
        left: string;
    };
};

function generateSparkle() {
    return {
        id: String(random(10000, 99999)),
        createdAt: Date.now(),
        color: colors[random(0, colors.length)],
        size: random(10, 20),
        style: {
            top: `${random(-60, 100)}%`,
            left: `${random(-60, 100)}%`
        }
    } as SparkleType;
}

type SparklesProps = Omit<ComponentPropsWithoutRef<'span'>, 'className'>;
const Sparkles = (props: SparklesProps) => {
    const [sparkles, setSparkles] = useState(range(3).map(() => generateSparkle()));
    useRandomInterval(
        () => {
            const sparkle = generateSparkle();
            const now = Date.now();
            const nextSparkles = sparkles.filter((sp) => {
                const delta = now - sp.createdAt;
                return delta < 750;
            });
            nextSparkles.push(sparkle);
            setSparkles(nextSparkles);
        },
        50,
        450
    );
    return (
        <Wrapper>
            {sparkles.map((sparkle) => (
                <Sparkle
                    key={sparkle.id}
                    color={sparkle.color}
                    size={sparkle.size}
                    style={sparkle.style}
                />
            ))}
            <span className="relative z-1" {...props} />
        </Wrapper>
    );
};

type SparkleProps = SparkleSvgProps & {
    style: CSSProperties;
};
const Sparkle = ({ size, color, style }: SparkleProps) => (
    <SparkleWrapper style={style}>
        <SparkleSvg size={size} color={color} />
    </SparkleWrapper>
);

type WrapperProps = Omit<ComponentPropsWithoutRef<'span'>, 'className'>;
const Wrapper = (props: WrapperProps) => <span className="relative inline-block" {...props} />;

type SparkleWrapperProps = Omit<ComponentPropsWithoutRef<'span'>, 'className'>;
const SparkleWrapper = (props: SparkleWrapperProps) => (
    <span className="comeInOut absolute z-2 block" {...props} />
);

type SparkleSvgProps = {
    size: SVGAttributes<SVGSVGElement>['width'] & SVGAttributes<SVGSVGElement>['height'];
    color: SVGAttributes<SVGSVGElement>['fill'];
};
const SparkleSvg = ({ size, color }: SparkleSvgProps) => {
    const path =
        'M26.5 25.5C19.0043 33.3697 0 34 0 34C0 34 19.1013 35.3684 26.5 43.5C33.234 50.901 34 68 34 68C34 68 36.9884 50.7065 44.5 43.5C51.6431 36.647 68 34 68 34C68 34 51.6947 32.0939 44.5 25.5C36.5605 18.2235 34 0 34 0C34 0 33.6591 17.9837 26.5 25.5Z';
    return (
        <svg className="spin block" width={size} height={size} viewBox="0 0 68 68" fill="none">
            <path d={path} fill={color} />
        </svg>
    );
};

export default Sparkles;
